import { FC, forwardRef, ReactNode, useRef } from "react";
import { Section as SectionStyled } from "../components/Layout/Section";
import { Container } from "../components/Layout/Container";
import styled from "@emotion/styled";
import { motion, useScroll, useTransform } from "framer-motion";

const Section = styled(SectionStyled)(() => ({
  backgroundColor: "rgb(0, 7, 31)",
  color: "#fff",
  minHeight: "150vh",
  position: "relative",
  paddingTop: 0,
}));

const FadeInContentStyled = styled(motion.div)(() => ({
  willChange: "transform",
  color: "#fff",
  position: "relative",
  width: "100%",
  paddingTop: 100,
}));

const FadeInTitleStyled = styled(motion.h1)(() => ({
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  color: "transparent",
  willChange: "transform",
  display: "flex",
  flexDirection: "column",
  textTransform: "uppercase",
  margin: 0,
  textAlign: "center",
  paddingTop: 100,
}));

const BigText = styled("span")(() => ({
  fontSize: 80,
  fontWeight: 500,
  color: "#fff",
  lineHeight: 0.9,
  "@media (min-width: 768px)": {
    fontSize: 160,
  },
}));

const SmallText = styled("span")(() => ({
  fontSize: 50,
  fontWeight: 400,
  lineHeight: 0.9,
  color: "#fff",
  "@media (min-width: 768px)": {
    fontSize: 80,
  },
}));

const FadeInTitle: FC = () => {
  const ref = useRef<HTMLHeadingElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "start end"],
    axis: "y",
  });

  let y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  let opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <FadeInTitleStyled style={{ opacity, y }} ref={ref}>
      <SmallText>Über</SmallText>
      <BigText>UNS</BigText>
    </FadeInTitleStyled>
  );
};

const ContentWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "3rem",
  width: "100%",
}));

const FadeInContent: FC<{ children?: ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLHeadingElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    axis: "y",
    offset: ["start end", "start start"],
  });

  let opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <FadeInContentStyled style={{ opacity }} ref={ref}>
      <ContentWrapper>{children}</ContentWrapper>
    </FadeInContentStyled>
  );
};

const Title = styled("h4")(() => ({
  fontSize: 40,
  margin: 0,
  fontFamily: "Oswald",
}));

const Subtitle = styled("h5")(() => ({
  margin: 0,
  fontSize: 30,
  fontWeight: 300,
  fontFamily: "Oswald",
  fontStyle: "italic",
  marginTop: -40,
}));

const Text = styled("p")(() => ({
  fontSize: 24,
  lineHeight: "30px",
  fontFamily: "Oswald",
  color: "rgba(255,255,255,.9)",
}));

export const About = forwardRef<HTMLDivElement, unknown>((props, ref) => {
  return (
    <Section ref={ref} id={"about"}>
      <Container>
        <FadeInTitle />
        <FadeInContent>
          <Title>CentreFilms – We bring your ideas to live</Title>
          <Subtitle>
            Wir lassen eure Ideen zu lebendigen Emotionen werden. Das ist
            Zentrum unserer Arbeit. Das ist unsere Leidenschaft.
          </Subtitle>
          <Text>
            who we are CentreFilms, die Film- und Fotoproduktion aus Dresden,
            wurde 2014 von einem jungen, kreativen, freischaffenden Team ins
            Leben gerufen und ist seit 2022 eine GmbH. Seitdem haben wir uns als
            Filmproduktion stets weiterentwickelt und vergrößert. <br />
            <br /> Unser Filmproduktionsteam vereint sich aus verschiedenen,
            kreativen und professionellen Fähigkeiten, Stärken und Kenntnissen
            der einzelnen Mitarbeiter. <br />
            Und diese breit gefächerte Aufstellung lässt aus jedem Projekt ein
            Gesamtkunstwerk entstehen und die verschiedensten Bereiche in der
            Film- und Fotobranche können wir dadurch abdecken. <br />
            <br />
            Sei es ein Werbefilm, Musikvideo, Livestream, Dokumentationen und
            vieles mehr. Dabei integrieren wir euch von der Vorproduktion, über
            die Produktion bis hin zur Postproduktion. Die Filmproduktion selbst
            können wir mit unseren Fotografen im Team fotografisch begleiten.
            Sprecht uns einfach dazu an. Uns findet ihr in unserem kreativen
            Film- und Fotostudio am Rande von Dresden auf 1200qm. Dort werden
            die Ideen kreiert, geplant, gedreht und geschnitten. <br />
            Durch unsere Hohlkehle inklusive professioneller Beleuchtungstechnik
            vor Ort sind wir in der Lage, sämtliche Projekte umzusetzen und zu
            realisieren. Aber CentreFilms ist nicht nur in Dresden aktiv.
            <br />
            <br /> Wir sind ebenso national, wie auch international als Filmteam
            unterwegs und finden nicht nur die besten Locations, sondern auch
            die besten Bilder für jegliche Aufnahmen im Film und Fotobereich.
            Neben unserem engeren Kundenstamm zählen gegenwärtig nicht nur die
            Deutsche Bahn und Sony, sondern auch Unternehmen wie die DHL,
            Sparkasse, Luxottica/ Sunglass Hut, wie auch die alljährlichen
            Kampagnen von „so geht sächsisch“ – womit wir 2017/18 zur ITB der
            „Golden City Award“ mit dem Film „BEEING HERE – A WINTERSTORY „ und
            „ WINTERMÄRCHEN“ mit Silber und Gold ausgezeichnet wurden.
            Zahlreiche weitere Kunden, Agenturen und Firmen vergrößern
            fortlaufend unser Repertoire und Referenzen. <br />
            <br />
            Solltet ihr Lust haben, das Film- und Fotostudio von CentreFilms zu
            nutzen, um eure Projekte und Ideen selbständig umzusetzen, so schaut
            auf unserem Menüpunkt „STUDIO/ VERMIETUNG vorbei.
          </Text>
        </FadeInContent>
      </Container>
    </Section>
  );
});
